import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import { auditApi } from "../../utils/api";
import { toast } from "react-toastify";

const PolicyPage = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const { tenant_id } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [file, setFile] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Log page access
    const logPageAccess = async () => {
      try {
        await auditApi.logActionSafe("VIEW_POLICY_PAGE", "POLICY", null, {
          page: "PolicyPage",
          tenant_id: tenant_id,
        });
      } catch (error) {
        console.warn("Audit logging failed:", error);
      }
    };

    // Fetch policies
    const fetchPolicies = async () => {
      try {
        const res = await axios.get(
          `${apiBase}/api/policies?tenant_id=${tenant_id}`
        );
        setPolicies(res.data);

        // Log successful fetch
        await auditApi.logActionSafe("LIST_POLICIES", "POLICY", null, {
          count: res.data.length,
          tenant_id: tenant_id,
        });
      } catch (err) {
        console.error("Error loading policies", err);

        // Log failed fetch
        await auditApi.logActionSafe("LIST_POLICIES_FAILED", "POLICY", null, {
          error: err.message,
          tenant_id: tenant_id,
        });
      } finally {
        setLoading(false);
      }
    };

    logPageAccess();
    fetchPolicies();
  }, [tenant_id, apiBase]);

  const handleUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setMessage(""); // Clear any previous messages

    try {
      const response = await axios.post(
        `${apiBase}/api/policies/upload?tenant_id=${tenant_id}&type=policy`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // Log successful upload
      await auditApi.logActionSafe(
        "UPLOAD_POLICY",
        "POLICY",
        response.data.document_id || null,
        {
          filename: file.name,
          fileSize: file.size,
          fileType: file.type,
          tenant_id: tenant_id,
          contains_phi: false, // Policies typically don't contain PHI
        }
      );

      setMessage("✅ Policy uploaded");
      toast.success("Policy uploaded successfully");

      // Refresh the policy list
      const res = await axios.get(
        `${apiBase}/api/policies?tenant_id=${tenant_id}`
      );
      setPolicies(res.data);

      // Clear the file input
      setFile(null);
      // Reset file input element
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Upload failed", err);
      setMessage("❌ Upload failed");
      toast.error("Failed to upload policy");

      // Log failed upload
      await auditApi.logActionSafe("UPLOAD_POLICY_FAILED", "POLICY", null, {
        filename: file.name,
        error: err.message,
        tenant_id: tenant_id,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?"))
      return;

    try {
      // Find the document details for audit logging
      const document = policies.find((p) => p.document_id === documentId);

      await axios.delete(
        `${apiBase}/api/policies/${documentId}?tenant_id=${tenant_id}`
      );

      // Log successful deletion
      await auditApi.logActionSafe("DELETE_POLICY", "POLICY", documentId, {
        document_name: document?.document_name,
        tenant_id: tenant_id,
        deleted_by: user?.email,
      });

      setPolicies((prev) =>
        prev.filter((doc) => doc.document_id !== documentId)
      );

      toast.success("Policy deleted successfully");
    } catch (err) {
      console.error("❌ Error deleting document:", err);
      alert("Failed to delete document.");

      // Log failed deletion
      await auditApi.logActionSafe(
        "DELETE_POLICY_FAILED",
        "POLICY",
        documentId,
        {
          error: err.message,
          tenant_id: tenant_id,
        }
      );
    }
  };

  const handleDocumentAccess = async (doc) => {
    try {
      // Log document access for audit trail (no PHI)
      await auditApi.logActionSafe(
        "ACCESS_POLICY_DOCUMENT",
        "POLICY",
        doc.document_id,
        {
          document_name: doc.document_name,
          tenant_id: tenant_id,
          contains_phi: false, // Policies typically don't contain PHI
          purpose_of_use: "OPERATIONS",
        }
      );

      // Open the document directly
      window.open(
        doc.public_url || doc.file_url,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (error) {
      console.error("Failed to log document access:", error);
      // Still open the document even if logging fails
      window.open(
        doc.public_url || doc.file_url,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  return (
    <div className="card p-4">
      <h4 className="mb-3">📑 Policy and Protocol</h4>

      {isAdmin && (
        <>
          <div className="mb-3">
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              accept=".pdf,.doc,.docx,.txt"
              disabled={uploading}
            />
            <button
              className="btn btn-primary ms-2"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading...
                </>
              ) : (
                "Upload"
              )}
            </button>
          </div>
          {message && <div className="alert alert-info">{message}</div>}
        </>
      )}

      <hr className="my-4" />

      <h5 className="mb-3">📂 Available Documents</h5>
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading policies...</span>
          </div>
          <p className="mt-2 text-muted">Loading policies...</p>
        </div>
      ) : (
        <ul className="list-group shadow-sm">
          {policies.length === 0 ? (
            <li className="list-group-item text-muted">
              No documents available.
            </li>
          ) : (
            policies.map((doc) => (
              <li
                key={doc.document_id}
                className="list-group-item d-flex justify-content-between align-items-center"
                style={{ padding: "0.75rem 1rem", fontSize: "1rem" }}
              >
                <a
                  href={doc.public_url || doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    // Log access when user clicks the link
                    handleDocumentAccess(doc);
                  }}
                  className="text-decoration-none"
                  style={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <i className="bi bi-file-earmark-text me-2"></i>
                  {doc.document_name}
                </a>

                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">
                    {new Date(doc.uploaded_at).toLocaleDateString()}
                  </small>

                  {isAdmin && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(doc.document_id)}
                      title="Delete document"
                    >
                      <i className="bi bi-trash"></i>
                      <span className="ms-1 d-none d-md-inline">Delete</span>
                    </button>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default PolicyPage;
